import React, { useEffect, useState } from "react";
import AdminHeader from "../../component/AdminHeader";
import StatCard from "../../component/StatCard";
import { Layers, Trophy } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { http } from "../../axios/axios";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import ActionButtons from "../../component/AdminActionButtons";
import { AnimatePresence } from "framer-motion";
import ApplicationModal from "./ApplicationModal";
import Pagination from "../../component/Pagination";

const AdminApplication = () => {
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("type") || "Job";
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const limit = 20;
  const [openModal, setOpenModal] = useState(false);

  const [applicationDetail, setApplicationDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [count, setCount] = useState({
    jobs: 0,
    courses: 0,
  });

  const getApplications = async () => {
    try {
      setLoading(true);
      const res = await http.get("/application", {
        params: {
          type: activeTab,
          page,
          limit,
        },
      });
      setApplications(res.data.data);
      setPagination(res.data.pagination);
      setCount(res.data?.counts);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (id, status) => {
    try {
      setDetailLoading(true);

      const res = await http.patch(`/application/${id}/status`, {
        status,
      });

      setApplicationDetail(res.data.data);
      setOpenModal(false);

      await getApplications();
    } catch (error) {
      console.log(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const deleteApplication = async (id) => {
    try {
      setLoading(true);

      const res = await http.delete(`/application/${id}`);

      console.log(res.data.message);

      if (applicationDetail?._id === id) {
        setOpenModal(false);
        setApplicationDetail(null);
      }

      await getApplications();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getApplicationById = async (id) => {
    try {
      setDetailLoading(true);
      const res = await http.get(`/application/${id}`);
      setApplicationDetail(res.data.data);
    } catch (error) {
      console.log(error);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    getApplications();
  }, [activeTab, page]);

  useEffect(() => {
    if (!searchParams.get("type")) {
      setSearchParams({ type: "Job" }, { replace: true });
    }
  }, [searchParams]);

  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200">
      <AdminHeader title={`Manage ${activeTab}s Applications`} />
      <div className="mb-8">
        <StatCard
          title={`Total ${activeTab}s`}
          count={pagination?.total}
          icon={Layers}
        />
      </div>
      <div className="flex gap-3 mb-6 items-center justify-center">
        {["Job", "Course"].map((tab) => (
          <button
            key={tab}
            onClick={() => {
              setPage(1);
              setSearchParams({ type: tab });
            }}
            className={`px-5 py-2 rounded-lg transition ${
              activeTab === tab
                ? "bg-blue-600 text-white"
                : "bg-slate-800 text-slate-300"
            }`}
          >
            {tab}s
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {applications?.length === 0 ? (
          <div
            className="p-8 rounded-2xl bg-white/5 border border-white/10
          text-center text-slate-400
          "
          >
            No Application Found
          </div>
        ) : (
          applications.map((application) => (
            <div
              key={application?._id}
              className="flex flex-col lg:flex-row lg:items-center lg:justify-between p-5 gap-4 rounded-xl bg-slate-900/40 border border-slate-800 hover:border-slate-700 hover:bg-slate-900/50 transition"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <div
                    className="h-12 w-12 sm:h-16 sm:w-16  bg-white p-2 flex items-center
                    rounded-full justify-center shrink-0"
                  >
                    <img
                      src={GetImageUrl(application?.profilePicture)}
                      className="max-h-full max-w-full object-contain "
                      loading="async"
                    />
                  </div>
                  <div>
                    <h3 className="text-md font-bold text-white">
                      {application.name?.charAt(0).toUpperCase() +
                        application.name?.slice(1)}
                    </h3>

                    <p className="text-sm text-gray-300">
                      Applied for:{" "}
                      <span className="font-medium">
                        {application.career?.title}
                      </span>
                    </p>

                    <p className="text-sm text-gray-400">{application.email}</p>
                    <p className="text-sm text-gray-400">
                      {application?.phoneNumber}
                    </p>

                    {application?.status && (
                      <span
                        className={`inline-flex items-center rounded px-3 py-1 mt-1 text-xs font-semibold text-white border ${
                          application.status === "Shortlisted"
                            ? "bg-emerald-500 border-emerald-600"
                            : application.status === "Reviewed"
                              ? "bg-amber-500 border-amber-600"
                              : application.status === "Rejected"
                                ? "bg-rose-500 border-rose-600"
                                : "bg-sky-500 border-sky-600"
                        }`}
                      >
                        {application.status}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <ActionButtons
                editText="View"
                onEdit={() => {
                  setApplicationDetail(null);
                  setOpenModal(true);
                  getApplicationById(application._id);
                }}
                onDelete={() => deleteApplication(application._id)}
              />
            </div>
          ))
        )}
      </div>
      {applications.length > 0 && (
        <Pagination
          page={page}
          totalPages={pagination?.totalPage}
          onPrevious={() => setPage((prev) => prev - 1)}
          onNext={() => setPage((prev) => prev + 1)}
        />
      )}

      <AnimatePresence>
        {openModal && (
          <ApplicationModal
            handleResolve={handleResolve}
            onClose={() => setOpenModal(false)}
            loading={detailLoading}
            applicationDetail={applicationDetail}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminApplication;
